// ─── areaConstants.js ────────────────────────────────────────────────────────

// ── Surface lists ─────────────────────────────────────────────────────────────

export const INTERNAL_SURFACES = [
  'Custom Surface Name', 'Ceilings', 'Walls', 'Floors', 'Windows', 'Doors', 'Skirting',
  // Condition-assessable surface. No SURFACE_CONFIG/GIRTH_DISPLAY entry → uses the
  // 'custom' fallback like 'Custom Surface Name'. Not in EXT_SURFACE_OFFSETS, so if measured
  // it routes to the Excel "Other" overflow; condition-only (PM-added) stays out of Excel.
  'Store Rooms',
];

export const EXTERNAL_SUBSTRATES = [
  'Custom Surface Name', 'Gutter', 'Fascia', 'Eaves', 'Soffit', 'Walls', 'Windows', 'Floors',
  'Downpipes', 'Slab Edge', 'Doors', 'Balustrade / Handrails', 'Posts', 'Roofs',
  // Condition-assessable surfaces. No SURFACE_CONFIG/GIRTH_DISPLAY entry → uses the
  // 'custom' fallback like 'Custom Surface Name'. Not in EXT_SURFACE_OFFSETS, so if measured
  // they route to the Excel "Other" overflow; condition-only (PM-added) stays out of Excel.
  // 'Play Equipment' was renamed from 'Timber Play Equipment' — the app serves aged care,
  // retirement living and commercial sites as well as childcare, and the equipment isn't
  // always timber. The old string stays as a legacy alias in SUBSTRATE_FAMILY (conditionLibrary.js)
  // since it is a map key in conditionBySubstrate on existing projects.
  'Play Equipment', 'Line Marking', 'Fences',
];

// ── Surface field config ──────────────────────────────────────────────────────

const STANDARD_FIELDS = ['length', 'width', 'height', 'quantity'];

export const SURFACE_CONFIG = {
  // ── Internal ────────────────────────────────────────────────────────────
  'Ceilings':              { fields: STANDARD_FIELDS, label: 'L × W × Qty', hasLabel: true },
  'Walls':                 { fields: STANDARD_FIELDS, label: 'L × H × Qty', hasLabel: true },
  'Floors':                { fields: STANDARD_FIELDS, label: 'L × W × Qty', hasLabel: true },
  'Skirting':              { fields: STANDARD_FIELDS, label: 'LM × Qty', hasLabel: true },
  'Doors':                 { fields: ['quantity', 'doorWidth', 'doorHeight', 'sides', 'frameWidth'], label: 'Qty · W × H · sides', isDoors: true },
  'Windows':               { fields: ['quantity', 'frameWidth', 'frameLength'], label: 'Qty · Frame W × Frame L', isWindows: true },
  'Stairs':                { fields: ['length', 'width', 'quantity'], label: 'L × W × Qty', hasLabel: true, hasLanding: true },
  // ── External ───────────────────────────────────────────────────────────
  'Gutter':                { fields: STANDARD_FIELDS, label: 'LM × Qty', hasLabel: true },
  'Fascia':                { fields: STANDARD_FIELDS, label: 'LM × Qty', hasLabel: true },
  'Eaves':                 { fields: STANDARD_FIELDS, label: 'L × W × Qty', hasLabel: true },
  'Soffit':                { fields: STANDARD_FIELDS, label: 'L × W × Qty', hasLabel: true },
  'Downpipes':             { fields: STANDARD_FIELDS, label: 'LM × Qty', hasLabel: true },
  'Slab Edge':             { fields: STANDARD_FIELDS, label: 'LM × Qty', hasLabel: true },
  'Balustrade / Handrails':{ fields: ['length', 'width', 'quantity'], label: 'L × W × Qty', hasLabel: true },
  'Posts':                 { fields: ['height', 'quantity'], label: 'H × Qty', hasLabel: true },
  'Roofs':                 { fields: ['length', 'width', 'quantity'], label: 'L × W × Qty', hasLabel: true, hasPitch: true },
  // ── Fallback ───────────────────────────────────────────────────────────
  'custom':                { fields: STANDARD_FIELDS, label: 'L × W × H × Qty', hasLabel: true },
};

// ── Girth display config ─────────────────────────────────────────────────────
// Controls how/whether girth appears in the surface settings row.
// inline: true = shown as a labelled dimension field above entries (replaces settings row girth)
// visible: false = hidden entirely (surface uses girth=1.0 always, users must not change)
// label/settingsLabel = relabelled in settings row (e.g. "Access Factor" for Eaves)

export const GIRTH_DISPLAY = {
  // Area surfaces — girth hidden (always 1.0)
  'Ceilings':               { visible: false },
  'Walls':                   { visible: false },
  'Floors':                  { visible: false },
  'Soffit':                  { visible: false },
  'Doors':                   { visible: false },
  'Stairs':                  { visible: false },
  'Balustrade / Handrails':  { visible: false },
  // Windows — girth managed by m²/LM toggle, not user-editable directly
  'Windows':                 { visible: false },
  // Linear surfaces — inline dimension field (writes to same girth setting)
  'Skirting':    { visible: true, inline: true, label: 'Board Height (m)', default: 0.2 },
  'Fascia':      { visible: true, inline: true, label: 'Board Width (m)', default: 0.3 },
  'Gutter':      { visible: true, inline: true, label: 'Gutter Width (m)', default: 0.3 },
  'Downpipes':   { visible: true, inline: true, label: 'Pipe Girth (m)', default: 0.3 },
  'Slab Edge':   { visible: true, inline: true, label: 'Edge Height (m)', default: 0.15 },
  'Posts':       { visible: true, inline: true, label: 'Post Girth (m)', default: 0.3 },
  // Eaves — industry adjustment factor, shown in settings with clear label
  'Eaves':       { visible: true, inline: false, settingsLabel: 'Access Factor' },
  // Roofs — already handled as Pitch dropdown (hasPitch flag)
  'Roofs':       { visible: true, inline: false },
};

// ── Wrap / Room Mode config ───────────────────────────────────────────────────

export const WRAP_CONFIG = {
  // Internal
  'Walls':             { sharedDim: 'height', dimLabel: 'Room Height (m)', runLabel: 'Wall length' },
  'Skirting':          { sharedDim: null,     dimLabel: null,               runLabel: 'Run length (LM)' },
  // External
  'Fascia':            { sharedDim: null,     dimLabel: null,                 runLabel: 'Run length (LM)' },
  'Soffit':            { sharedDim: 'width',  dimLabel: 'Soffit Width (m)',   runLabel: 'Run length' },
  'Gutter':            { sharedDim: null,     dimLabel: null,                 runLabel: 'Run length (LM)' },
  'custom':            { sharedDim: 'height', dimLabel: 'Height (m)',         runLabel: 'Run length' },
};

export function getWrapConfig(name) {
  return WRAP_CONFIG[name] || null;
}

// ── Production rates ──────────────────────────────────────────────────────────

export const PRODUCTION_RATES = {
  // Internal
  'Ceilings':          { rate: 16, coats: 2, girth: 1.0 },
  'Walls':             { rate: 16, coats: 2, girth: 1.0 },
  'Floors':            { rate: 16, coats: 2, girth: 1.0 },
  'Skirting':          { rate: 10, coats: 2, girth: 0.2 },
  'Windows':           { rate: 10, coats: 2, girth: 0.2 },
  'Doors':             { rate:  7, coats: 3, girth: 1.0 },
  'Door frames':       { rate:  7, coats: 3, girth: 0.3 },
  'Stairs':            { rate: 12, coats: 2, girth: 1.0 },
  // External
  'Gutter':            { rate: 12, coats: 2, girth: 0.3 },
  'Fascia':            { rate: 16, coats: 2, girth: 0.3 },
  'Eaves':             { rate: 16, coats: 2, girth: 0.8 },
  'Soffit':            { rate: 16, coats: 2, girth: 1.0 },
  'Downpipes':         { rate: 16, coats: 2, girth: 0.3 },
  'Slab Edge':         { rate: 10, coats: 2, girth: 0.15 },
  'Balustrades/Handrails': { rate: 7, coats: 2, girth: 1.0 },
  'Balustrade / Handrails': { rate: 7, coats: 2, girth: 1.0 },
  'Posts':             { rate: 16, coats: 2, girth: 0.3 },
  'Roofs':             { rate: 16, coats: 2, girth: 1.15 },
  // Fallback
  '_default':          { rate: 16, coats: 2, girth: 1.0 },
};

export const EXT_PRODUCTION_RATE = { rate: 16, coats: 2 };

export function getSurfaceConfig(name) {
  return SURFACE_CONFIG[name] || SURFACE_CONFIG['custom'];
}

// ── Preliminary items (per-area tags — no values in app, names exported to Excel) ─

export const PRELIMINARY_ITEMS = [
  'Scaffolding',
  'Traffic Management',
  'Security Fencing',
  'Waste Removal',
  'Permit Fees',
  'Third Party Contractor',
];

// ── Application method config ─────────────────────────────────────────────────

export const METHOD_CONFIG = {
  'brush_roll': { label: 'Brush/Roll', icon: '🖌️', defaultSpeed: null, defaultPrep: 0 },
  'spray':      { label: 'Spray',      icon: '🔫', defaultSpeed: 30,   defaultPrep: 0.25 },
  'abseil':     { label: 'Abseil',     icon: '🧗', defaultSpeed: 12,   defaultPrep: 0.40 },
};

export const METHOD_OPTIONS = Object.entries(METHOD_CONFIG).map(([key, cfg]) => ({
  key, ...cfg,
}));

// ── Condition rating (RAG) for the Condition Report ───────────────────────────
// Mirrors the report's Condition Index: 1 Poor (red), 2 Fair (amber), 3 Good (green).
// `fill` is the cell shading the Word export will apply to the "Condition Analysis" cell.
export const CONDITION_LEVELS = [
  { key: 'poor', severity: 1, label: 'Poor', fill: 'FF0000', short: 'P' },
  { key: 'fair', severity: 2, label: 'Fair', fill: 'F5A623', short: 'F' },
  { key: 'good', severity: 3, label: 'Good', fill: '21A04A', short: 'G' },
];

export const CONDITION_BY_KEY = Object.fromEntries(
  CONDITION_LEVELS.map(c => [c.key, c])
);

// ─── Condition Report v3 — RAG design system (single source of truth) ─────────
// New v3 report tokens. The CURRENT Word export keeps its legacy fills
// (FF0000/F5A623/21A04A) untouched — these apply to the v3 report only
// (Jason, Jul 2026). App condition keys (poor/fair/good) map to RAG here so the
// rollup logic, the v3 fill engine and any future PDF companion share ONE
// definition. `fill` is deliberately distinct amber (#E67E22 ≠ brand orange).
export const CONDITION_TO_RAG = { poor: 'red', fair: 'amber', good: 'green' };

// `onFill` is contrast-tuned per RAG (v3 only — legacy WordExport has its own fills).
// White clears WCAG AA on the dark red (5.44:1) but only reaches ~2.85:1 on the lighter
// amber/green fills, so those carry dark text (#1A1A1A → 6.11:1 / 6.06:1). Keeps the
// locked fill hues (§5.1/§12.3) while meeting the spec's AA requirement.
export const RAG_TOKENS = {
  red:   { key: 'red',   label: 'Poor', fill: 'C0392B', onFill: 'FFFFFF', rank: 3 },
  amber: { key: 'amber', label: 'Fair', fill: 'E67E22', onFill: '1A1A1A', rank: 2 },
  green: { key: 'green', label: 'Good', fill: '27AE60', onFill: '1A1A1A', rank: 1 },
};

// Overall Site Condition rollup thresholds (proportional/threshold model, §7.1).
// Tunable named constants — shares are of all assessed elements. Calibrate on real data.
export const OVERALL_RAG_THRESHOLDS = { redShare: 0.10, redAmberShare: 0.25 };

// RAG-derived default Priority (drives dashboard sort only — never shown as text;
// assessor override allowed, §7.6).
export const RAG_DEFAULT_PRIORITY = { red: 'high', amber: 'medium', green: 'low' };
export const PRIORITY_RANK = { high: 3, medium: 2, low: 1 };

// ─── RAG → painting recommendation (single source of truth) ───────────────────
// Green = no painting required. Amber = consider painting, but not essential.
// Red = repaint as soon as possible. Deliberately simple — no maintenance-strategy
// picker, no per-area override. Every recommendation-bearing surface in the report
// (cover hero, asset summary, Higgins Recommendation callouts, dashboard/register
// Action column, generated condition-note closer) reads from this ONE object, so
// the report never mixes vocabularies.
//   action — short label for the Action column / asset-summary band / callout heading
//   hero   — cover-page hero sub-line
//   lead   — plain-language sentence for the Higgins Recommendation callout body
//   closer — sentence appended to a generated per-substrate condition note
export const RAG_RECOMMENDATION = {
  red: {
    action: 'Repaint ASAP',
    hero: 'Repaint recommended as soon as possible',
    lead: 'This surface should be repainted as soon as possible.',
    closer: 'Recommended to be repainted as soon as possible.',
  },
  amber: {
    action: 'Consider Painting',
    hero: 'Painting recommended, not essential',
    lead: 'Painting is recommended but not essential — plan for it when convenient.',
    closer: 'Painting recommended but not essential — a reduced scope may be considered.',
  },
  green: {
    action: 'No Painting Required',
    hero: 'No painting required',
    lead: 'No painting is required at this time.',
    closer: 'No painting required — sound condition.',
  },
};

// ─── Sector classification (for AI sector lead-in, §6.2) ──────────────────────
// Project-level (optional per-building for portfolio). Unset = single unnamed sector.
export const SECTORS = [
  { key: 'retirement_living', label: 'Retirement Living' },
  { key: 'aged_care',         label: 'Aged Care' },
  { key: 'childcare',         label: 'Childcare' },
  { key: 'commercial',        label: 'Commercial' },
];
export const SECTOR_BY_KEY = Object.fromEntries(SECTORS.map(s => [s.key, s]));

// ─── Site Items (project-level, counted non-painting scope) ───────────────────
// Counted fixtures/fittings the assessor tallies while walking the site — supply,
// replace or remove before painting. Client- and sector-neutral: a curated default
// list plus a free-form 'customItems' escape hatch so a new sector needs no code
// change. Driving the UI and report off this list means a future item is a
// one-line change here.
export const SITE_ITEM_STATUS = [
  { key: 'counted',          label: 'Counted' },
  { key: 'nil',              label: 'Nil / none required' },
  { key: 'client_direction', label: 'At client direction' },
  { key: 'by_others',        label: 'Already done by others' },
];

export const SITE_ITEMS = [
  { key: 'fingerGuards',         label: 'Finger guards',          kind: 'count', markup: true },
  { key: 'doorCaps',             label: 'Door caps',              kind: 'count', markup: true },
  { key: 'kickerPlates',         label: 'Stainless kicker plates',kind: 'count', markup: true },
  { key: 'soapDispensers',       label: 'Soap dispensers',        kind: 'count' },
  { key: 'paperTowelDispensers', label: 'Paper towel dispensers', kind: 'count' },
  { key: 'toiletRollHolders',    label: 'Toilet roll holders',    kind: 'count' },
  { key: 'blinds',               label: 'Blinds to remove',       kind: 'count', photos: true,
    hint: '3yo and below education rooms maintain blinds' },
  { key: 'displayBoards',        label: 'Display boards to remove', kind: 'rows',
    cols: [ {k:'location',label:'Room & location'}, {k:'qty',label:'Qty',numeric:true},
            {k:'sizeMm',label:'Size (mm)'}, {k:'comments',label:'Comments'} ] },
  { key: 'halfHeightDoors',      label: 'Half-height door alterations', kind: 'rows',
    cols: [ {k:'location',label:'Room & location'}, {k:'qty',label:'# to alter',numeric:true} ] },
  { key: 'removeReplaceMisc',    label: 'Remove & replace misc.', kind: 'rows',
    cols: [ {k:'item',label:'Item'}, {k:'location',label:'Room & location'},
            {k:'hoursMaterials',label:'Hours / materials'} ] },
  // Escape hatch so a sector or client with items outside the curated list needs no
  // code change. Keep it last.
  { key: 'customItems',          label: 'Other counted items',   kind: 'rows',
    cols: [ {k:'item',label:'Item'}, {k:'location',label:'Location'},
            {k:'qty',label:'Qty',numeric:true}, {k:'note',label:'Notes'} ] },
];

// ─── Internal Flooring register (project-level, §4) ────────────────────────
// Floor coverings graded for replacement — a different thing from the 'Floors'
// painting substrate. Client- and sector-neutral: a curated product list plus an
// 'Other…' free-text option in the UI, so a new sector needs no code change.
export const FLOOR_PRODUCTS = [
  'Sheet Vinyl', 'Wet Area Sheet Vinyl', 'Vinyl Plank', 'Timber Look Vinyl',
  'Square Vinyl Tiles', 'Timber Laminate', 'Carpet', 'Carpet Tiles',
  'Safety Vinyl', 'Polished Concrete', 'Tiles', 'Timber Decking',
];

export const FLOOR_RECOMMENDATIONS = [
  { key: 'retain',   label: 'Retain — no work' },
  { key: 'clean',    label: 'Deep clean / restore' },
  { key: 'patch',    label: 'Patch / partial replace' },
  { key: 'replace',  label: 'Replace' },
  { key: 'discuss',  label: 'Discuss options with client' },
];

// ─── Substrate finish + colour-scheme compliance (P3) ──────────────────────────
// Substrate finish — what the surface IS, independent of what condition it's in.
// Drives the "no work required" exclusion outcome. Distinct from the `wrong_colour`
// defect tag, which describes a fault; this describes the substrate.
export const SUBSTRATE_FINISHES = [
  { key: 'painted',     label: 'Painted',                 excludes: false },
  { key: 'stained',     label: 'Stained',                 excludes: false },
  { key: 'prefinished', label: 'Prefinished / Colorbond', excludes: true  },
  { key: 'unpainted',   label: 'Unpainted / bare',        excludes: true  },
  { key: 'galvanised',  label: 'Galvanised / mill',       excludes: true  },
];
export const FINISH_BY_KEY = Object.fromEntries(SUBSTRATE_FINISHES.map(f => [f.key, f]));
export const EXCLUDING_FINISHES = new Set(
  SUBSTRATE_FINISHES.filter(f => f.excludes).map(f => f.key)
);

// ─── Treatment level → Option 1 / Option 2 split (P4) ──────────────────────────
// Recommended treatment per substrate — the Option 2 (reduced-scope) call.
// `coats` is advisory metadata for the estimator, NOT used in any calculation.
// `full` marks the treatments that already equal full specification, so a substrate
// set to one of these produces no Option 1 / Option 2 difference.
export const TREATMENT_LEVELS = [
  { key: 'no_work',     label: 'No work required',      coats: 0, full: false },
  { key: 'wash_only',   label: 'Wash / de-cob only',    coats: 0, full: false },
  { key: 'one_coat',    label: '1 coat',                coats: 1, full: false },
  { key: 'two_coat',    label: '2 coats (full spec)',   coats: 2, full: true  },
  { key: 'three_coat',  label: '3 coats (adhesion)',    coats: 3, full: true  },
  { key: 'restain',     label: 'Re-stain',              coats: 1, full: true  },
];
export const TREATMENT_BY_KEY = Object.fromEntries(TREATMENT_LEVELS.map(t => [t.key, t]));

// RAG-derived default the assessor confirms or overrides, mirroring RAG_DEFAULT_PRIORITY.
export const RAG_DEFAULT_TREATMENT = { red: 'two_coat', amber: 'one_coat', green: 'wash_only' };

// The Option 1 baseline: full specification for anything not excluded. 'restain' is the
// full-spec baseline for stained substrates (timber play equipment, dividing fences).
export const FULL_SPEC_TREATMENT = 'two_coat';
export const FULL_SPEC_TREATMENT_STAINED = 'restain';

// ─── Client referral register (P5) ──────────────────────────────────────────
// Defect tags that SUGGEST (never auto-enable) the "Refer to client" toggle —
// structural make-good, not cosmetic paint-film defects. The assessor decides;
// the app never decides for them.
export const REFERRAL_SUGGEST_DEFECTS = new Set([
  'rot', 'structural_crack', 'spalling', 'rising_damp', 'sagging_sheet',
  'rust_perforation', 'drummy_render',
]);
